import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';

import { listOrganizations } from '@/features/organizations/services/api';
import { organizationKeys } from '@/features/organizations/services/queryKeys';
import { ApiClientError } from '@/lib/api';
import {
    AppBootstrapContext,
    type AppBootstrapContextValue,
    type AppBootstrapStatus
} from '@/providers/AppBootstrapProvider.context';
import { setAppBootstrapSnapshot } from '@/providers/app-bootstrap-snapshot';
import { useAuth } from '@/providers/useAuth';

interface BootstrapSnapshot {
    status: AppBootstrapStatus;
    isAuthenticated: boolean;
    organizationsCount: number | null;
    error: unknown | null;
}

export function AppBootstrapProvider({ children }: PropsWithChildren) {
    const queryClient = useQueryClient();
    const { isReady: isAuthReady, isAuthenticated, error: authError } = useAuth();
    const [snapshot, setSnapshot] = useState<BootstrapSnapshot>({
        status: 'idle',
        isAuthenticated: false,
        organizationsCount: null,
        error: null
    });
    const runIdRef = useRef(0);
    const bootstrappedSessionRef = useRef<string | null>(null);

    const resolveSessionKey = useCallback(() => {
        if (!isAuthReady) {
            return 'pending-auth';
        }

        return isAuthenticated ? 'authenticated' : 'unauthenticated';
    }, [isAuthenticated, isAuthReady]);

    const runBootstrap = useCallback(async () => {
        const runId = ++runIdRef.current;

        if (!isAuthReady) {
            setSnapshot({
                status: 'auth-initializing',
                isAuthenticated: false,
                organizationsCount: null,
                error: null
            });
            return;
        }

        if (authError) {
            setSnapshot({
                status: 'error',
                isAuthenticated: false,
                organizationsCount: null,
                error: authError
            });
            return;
        }

        if (!isAuthenticated) {
            bootstrappedSessionRef.current = 'unauthenticated';
            setSnapshot({
                status: 'awaiting-auth',
                isAuthenticated: false,
                organizationsCount: null,
                error: null
            });
            return;
        }

        bootstrappedSessionRef.current = 'authenticated';
        setSnapshot((current) => ({
            status: 'bootstrapping',
            isAuthenticated: true,
            organizationsCount: current.organizationsCount,
            error: null
        }));

        try {
            const organizations = await queryClient.ensureQueryData({
                queryKey: organizationKeys.list(),
                queryFn: listOrganizations,
                retry: false
            });

            if (runId !== runIdRef.current) {
                return;
            }

            bootstrappedSessionRef.current = 'authenticated';
            setSnapshot({
                status: 'ready',
                isAuthenticated: true,
                organizationsCount: organizations.length,
                error: null
            });
        } catch (error) {
            if (runId !== runIdRef.current) {
                return;
            }

            if (error instanceof ApiClientError) {
                if (error.status === 401) {
                    setSnapshot({
                        status: 'error',
                        isAuthenticated: false,
                        organizationsCount: null,
                        error: new Error('Authentication expired during bootstrap. Please sign in again.')
                    });
                    return;
                }

                if (!error.recoverable) {
                    bootstrappedSessionRef.current = 'authenticated';
                    setSnapshot({
                        status: 'ready',
                        isAuthenticated: true,
                        organizationsCount: null,
                        error: null
                    });
                    return;
                }
            }

            setSnapshot({
                status: 'error',
                isAuthenticated: true,
                organizationsCount: null,
                error
            });
        }
    }, [authError, isAuthReady, isAuthenticated, queryClient]);

    useEffect(() => {
        const sessionKey = resolveSessionKey();

        if (bootstrappedSessionRef.current === sessionKey) {
            return;
        }

        queueMicrotask(() => {
            void runBootstrap();
        });
    }, [resolveSessionKey, runBootstrap]);

    const value = useMemo<AppBootstrapContextValue>(
        () => ({
            status: snapshot.status,
            isBootstrapping:
                snapshot.status === 'auth-initializing' ||
                snapshot.status === 'awaiting-auth' ||
                snapshot.status === 'bootstrapping',
            isReady: snapshot.status === 'ready',
            isAuthenticated: snapshot.isAuthenticated,
            hasResolvedAuth: isAuthReady,
            organizationsCount: snapshot.organizationsCount,
            error: snapshot.error,
            retry: async () => {
                bootstrappedSessionRef.current = null;
                await runBootstrap();
            }
        }),
        [
            isAuthReady,
            runBootstrap,
            snapshot.error,
            snapshot.isAuthenticated,
            snapshot.organizationsCount,
            snapshot.status
        ]
    );

    useEffect(() => {
        setAppBootstrapSnapshot({
            status: snapshot.status,
            isAuthenticated: snapshot.isAuthenticated,
            organizationsCount: snapshot.organizationsCount
        });
    }, [snapshot.isAuthenticated, snapshot.organizationsCount, snapshot.status]);

    return <AppBootstrapContext.Provider value={value}>{children}</AppBootstrapContext.Provider>;
}
